/**
 * Step 1 do wizard ZTNA — Tenant + DNS.
 *
 * Selects encadeados:
 *   1. Tenant (cross-account discovery via /admin/ztna/tenants).
 *   2. Zona Route53 (filtrada por tenant_id selecionado).
 *   3. Modo de registro: criar novo (subdomain inline) ou reaproveitar.
 *
 * Pre-flight inline simples ao selecionar tenant ("Validando IAM role...").
 * Pre-flight detalhado completo roda no Step 4 (PreflightChecklist).
 */
import { useEffect, useMemo } from 'react'
import {
  Alert,
  Card,
  Group,
  Loader,
  Radio,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { IconCheck, IconAlertTriangle } from '@tabler/icons-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ztnaApi } from '../api'
import type { Environment } from '../types'
import { HostnamePreview } from './HostnamePreview'
import { TenantPrepareBanner } from './TenantPrepareBanner'
import {
  CLASSIFICACOES_DE_ASSUME,
  classificacaoDoErro,
  zoneErrorMessage,
} from './zoneErrors'
import {
  buildPublicHostname,
  deriveSubdomainFromRecord,
  type WizardStep1Data,
} from './wizardState'

interface Props {
  data: WizardStep1Data
  onChange: (next: WizardStep1Data) => void
}

export function Step1TenantDns({ data, onChange }: Props) {
  const queryClient = useQueryClient()
  const tenantsQuery = useQuery({
    queryKey: ['ztna', 'tenants'],
    queryFn: () => ztnaApi.listTenants(),
    staleTime: 60_000,
  })

  // Sub-projeto 4 Lote 1: tenant precisa estar preparado (role criada)
  // antes de listar zonas Route53. Quando role_arn é null, banner aparece
  // no lugar do dropdown de zonas.
  const selectedTenant = useMemo(() => {
    if (data.tenant_id == null) return null
    return (tenantsQuery.data ?? []).find((t) => t.id === data.tenant_id) ?? null
  }, [tenantsQuery.data, data.tenant_id])
  const needsPrepare =
    selectedTenant != null && !selectedTenant.ztna_provision_role_arn

  // Gate zones query com !needsPrepare: tenant sem role_arn nao tem o que
  // listar, e o staleTime de 60s manteria o erro cacheado mesmo apos clicar
  // Preparar.
  //
  // NAO confundir com o 503 de assume_role — aquele vinha do salto DIRETO na
  // role do tenant, que a replataforma quebrou e o duplo salto via
  // ConsoleAICrossAccountRole corrigiu em 2026-08-06 (aws/sts.py). O
  // comentario antigo aqui atribuia aquele AccessDenied ao prepare, e foi essa
  // leitura errada que fez o bug durar dias.
  const zonesQuery = useQuery({
    queryKey: ['ztna', 'zones', data.tenant_id],
    queryFn: () => ztnaApi.listZones(data.tenant_id!),
    enabled: data.tenant_id != null && !needsPrepare,
    staleTime: 60_000,
  })

  // TASK-9892: a consulta de zonas E a medicao — ela passa por
  // _require_tenant_session no backend, que faz o duplo salto. O verde sai do
  // resultado dela, nunca da coluna do banco; enquanto ela nao respondeu, cinza.
  // Antes o verde vinha de `tenant_id != null && !needsPrepare` e dizia "sera
  // validado" em tempo futuro — promessa com peso visual de medicao, empilhada
  // sobre o vermelho da mesma tela.
  const classificacaoZonas = classificacaoDoErro(zonesQuery.error)
  const precisaReparo =
    data.tenant_id != null &&
    !needsPrepare &&
    classificacaoZonas != null &&
    CLASSIFICACOES_DE_ASSUME.has(classificacaoZonas)
  const medindoAcesso =
    data.tenant_id != null && !needsPrepare && zonesQuery.isFetching
  const acessoConferido =
    data.tenant_id != null && !needsPrepare && zonesQuery.isSuccess

  const recordsQuery = useQuery({
    queryKey: ['ztna', 'records', data.tenant_id, data.zone_id],
    queryFn: () => ztnaApi.listRecords(data.tenant_id!, data.zone_id),
    enabled: data.tenant_id != null && Boolean(data.zone_id) && data.record_mode === 'reuse',
    staleTime: 30_000,
  })

  // dns-lookup query vive no WizardContainer (escopo do wizard inteiro)
  // — se rodasse aqui e o user avancasse pro Step 2/3 antes da query
  // completar, o useEffect de propagacao desmontaria junto com o Step
  // e o resultado nunca chegaria em state.dnsLookup → Step 3 sem
  // auto-fill.

  const tenantOptions = useMemo(() => {
    return (tenantsQuery.data ?? []).map((t) => ({
      value: String(t.id),
      label: `${t.name} — conta ${t.account_id ?? '—'} (${t.environment})`,
    }))
  }, [tenantsQuery.data])

  const zoneOptions = useMemo(() => {
    return (zonesQuery.data ?? []).map((z) => ({
      value: z.id,
      label: `${z.name}  ·  ${z.id}${z.private ? ' (privada)' : ''}`,
    }))
  }, [zonesQuery.data])

  const recordOptions = useMemo(() => {
    return (recordsQuery.data ?? [])
      .filter((r) => r.type === 'A' || r.type === 'CNAME')
      // value = subdomain relativo derivado ('@' para o apex) — casa com
      // data.subdomain, então a seleção sobrevive ao draft e o hostname
      // nunca duplica a zona (TASK-2778).
      .map((r) => ({
        value: deriveSubdomainFromRecord(r.name, data.zone_name),
        label: `${r.name} (${r.type})`,
      }))
      .filter((o) => o.value !== '')
  }, [recordsQuery.data, data.zone_name])

  // Recalcula public_hostname sempre que subdomain ou zone mudarem.
  useEffect(() => {
    const hostname = buildPublicHostname(data.subdomain, data.zone_name)
    if (hostname !== data.public_hostname) {
      onChange({ ...data, public_hostname: hostname })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.subdomain, data.zone_name])

  function handleTenantChange(value: string | null) {
    if (!value) {
      onChange({
        ...data,
        tenant_id: null,
        tenant_name: null,
        tenant_slug: null,
        environment: null,
        zone_id: '',
        zone_name: null,
        public_hostname: '',
      })
      return
    }
    const id = Number(value)
    const tenant = (tenantsQuery.data ?? []).find((t) => t.id === id)
    if (!tenant) return
    onChange({
      ...data,
      tenant_id: id,
      tenant_name: tenant.name,
      tenant_slug: tenant.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      environment: (tenant.environment.toUpperCase() as Environment) ?? null,
      zone_id: '',
      zone_name: null,
      public_hostname: '',
    })
  }

  function handleZoneChange(value: string | null) {
    if (!value) {
      onChange({ ...data, zone_id: '', zone_name: null, public_hostname: '' })
      return
    }
    const zone = (zonesQuery.data ?? []).find((z) => z.id === value)
    onChange({
      ...data,
      zone_id: value,
      zone_name: zone?.name ?? null,
      public_hostname: buildPublicHostname(data.subdomain, zone?.name ?? null),
    })
  }

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="md">
        <div>
          <Title order={4}>Selecione o tenant e o registro DNS</Title>
          <Text size="sm" c="dimmed" mt={4}>
            O hostname público sera resolvido automaticamente quando você
            selecionar tenant e zona Route53.
          </Text>
        </div>

        {tenantsQuery.error ? (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            Falha ao carregar tenants. Verifique a permissão
            <code> ztna:onboard:provision</code>.
          </Alert>
        ) : null}

        <Select
          label="Tenant"
          placeholder={tenantsQuery.isLoading ? 'Carregando tenants...' : 'Escolha o tenant'}
          required
          searchable
          data={tenantOptions}
          value={data.tenant_id != null ? String(data.tenant_id) : null}
          onChange={handleTenantChange}
          disabled={tenantsQuery.isLoading}
          rightSection={tenantsQuery.isLoading ? <Loader size="xs" /> : null}
          comboboxProps={{ shadow: 'md', withinPortal: true }}
        />

        {medindoAcesso ? (
          <Group gap={6} c="dimmed" data-testid="acesso-verificando">
            <Loader size="xs" />
            <Text size="xs">
              Verificando o acesso a conta de <code>{data.tenant_name}</code>...
            </Text>
          </Group>
        ) : null}

        {acessoConferido ? (
          <Group gap={6} c="green" data-testid="acesso-conferido">
            <IconCheck size={14} />
            <Text size="xs">
              Acesso a conta de <code>{data.tenant_name}</code> conferido agora
              — a ConsoleAIZtnaProvisionRole foi assumida.
            </Text>
          </Group>
        ) : null}

        {(needsPrepare || precisaReparo) && selectedTenant ? (
          <TenantPrepareBanner
            tenantId={selectedTenant.id}
            tenantName={selectedTenant.name}
            modo={needsPrepare ? 'preparar' : 'reparo'}
            onPrepared={() => {
              // Invalida cache de tenants pra refetch e popular role_arn.
              queryClient.invalidateQueries({ queryKey: ['ztna', 'tenants'] })
              // E zones — banner aparece quando role_arn=null OU quando role
              // existe mas trust nao inclui SSMInstanceProfile (caso de roles
              // criadas pre-MR !221). Em ambos os casos, prepare atualiza
              // trust+policies; zones cached com erro precisa re-fetch.
              queryClient.invalidateQueries({
                queryKey: ['ztna', 'zones', selectedTenant.id],
              })
              queryClient.invalidateQueries({
                queryKey: ['ztna', 'records', selectedTenant.id],
              })
            }}
          />
        ) : null}

        {zonesQuery.error ? (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            {zoneErrorMessage(zonesQuery.error)}
          </Alert>
        ) : null}

        <Select
          label="Zona Route53"
          placeholder={
            data.tenant_id == null
              ? 'Selecione um tenant primeiro'
              : needsPrepare
                ? 'Prepare o tenant primeiro'
                : zonesQuery.isLoading
                  ? 'Carregando zonas...'
                  : 'Escolha a zona'
          }
          required
          searchable
          data={zoneOptions}
          value={data.zone_id || null}
          onChange={handleZoneChange}
          disabled={data.tenant_id == null || needsPrepare || zonesQuery.isLoading}
          rightSection={zonesQuery.isLoading ? <Loader size="xs" /> : null}
          comboboxProps={{ shadow: 'md', withinPortal: true }}
        />

        <Radio.Group
          label="Registro DNS"
          required
          value={data.record_mode}
          onChange={(value) =>
            onChange({ ...data, record_mode: value === 'reuse' ? 'reuse' : 'create' })
          }
        >
          <Group gap="md" mt={4}>
            <Radio value="create" label="Criar novo (wildcard A na zona acima)" />
            <Radio
              value="reuse"
              label={`Reaproveitar existente${
                recordsQuery.data ? ` (${recordOptions.length} aproveitáveis)` : ''
              }`}
              disabled={!data.zone_id}
            />
          </Group>
        </Radio.Group>

        {data.record_mode === 'create' ? (
          <TextInput
            label="Subdominio"
            placeholder="ex: portal, api, admin"
            required
            value={data.subdomain}
            onChange={(e) => onChange({ ...data, subdomain: e.currentTarget.value })}
            description={
              data.zone_name
                ? `Gera ${data.subdomain || '<subdomain>'}.${data.zone_name}`
                : 'Selecione uma zona primeiro'
            }
          />
        ) : (
          <Select
            label="Registro existente"
            placeholder={
              recordsQuery.isLoading ? 'Carregando registros...' : 'Selecione um registro'
            }
            required
            searchable
            data={recordOptions}
            value={data.subdomain || null}
            onChange={(value) => {
              if (!value || !data.zone_name) return
              // value já É o subdomain relativo derivado ('@' = apex) — ver recordOptions
              onChange({ ...data, subdomain: value })
            }}
            disabled={recordsQuery.isLoading}
            rightSection={recordsQuery.isLoading ? <Loader size="xs" /> : null}
            comboboxProps={{ shadow: 'md', withinPortal: true }}
          />
        )}

        <HostnamePreview
          publicHostname={data.public_hostname}
          showCanonical={false}
        />
      </Stack>
    </Card>
  )
}
